import { useEffect, useState } from 'react';
import { CBCC_BANNERS } from '../../content/content';
import { cbccRouteApi } from '../route-api';
import { CBCC_USER_FLOW, HOME_SCREEN_NAME } from '../types';

// Banner height (px). Exported so the layout can expose it as a CSS var
// (--cbcc-banner-h) that the hero chrome reads to offset its logo below the
// fixed banner — WITHOUT adding document height (so no extra scroll).
export const CBCC_BANNER_HEIGHT = 44;

// Window event the layout fires AFTER the banner's presence changes (e.g. the
// onboarding bar is dismissed), so --cbcc-banner-h has flipped. The hero stage
// listens and re-runs ScrollTrigger.refresh(): the hero column's banner padding
// collapses, which shifts the GSAP-measured card slot and every downstream beat
// (dotted frame / gallery / carousel stations) — those measurements are only
// recomputed on a refresh, so without this the journey lands mis-placed. A
// plain CustomEvent keeps the layout free of any GSAP dependency (it also
// renders the no-GSAP manage/rejected views).
export const CBCC_BANNER_CHANGE_EVENT = 'cbcc:banner-change';

// Which top banner (if any) applies for the current CVP state. Resolved from the
// server loader so it's known on first paint.
//   - REJECTED (status === 'REJECTED'): RED bar, NOT dismissible. The layout
//     also renders the locked first-screen view for this state.
//   - ONBOARDING resume (userFlow === 'ONBOARDING' && showResumePopup): YELLOW
//     bar, DISMISSIBLE (cross). The full journey + CTA stay.
//   - none: no banner.
// A shared hook (not internal state) so the LAYOUT owns visibility — it needs to
// know whether a banner shows to expose the banner-height CSS var and to own the
// dismiss state for the onboarding bar.
export type CbccBannerTone = 'rejected' | 'onboarding';

export function useCbccBannerTone(): CbccBannerTone | null {
	const { screens } = cbccRouteApi.useLoaderData();
	const homeScreen = screens?.find((s) => s.name === HOME_SCREEN_NAME);
	if (homeScreen?.status === 'REJECTED') return 'rejected';
	if (
		homeScreen?.userFlow === CBCC_USER_FLOW.ONBOARDING &&
		homeScreen?.showResumePopup
	) {
		return 'onboarding';
	}
	return null;
}

// Presentational banner. FIXED overlay at the very top — it adds NO document
// height, so it can't create the extra vertical scroll an in-flow banner did
// (the 44px of free scroll before the pinned hero engaged). The hero chrome is
// offset below it via the --cbcc-banner-h CSS var (set by the layout), so the
// logo never overlaps it. On the scrollable onboarding journey the banner FADES
// OUT as the user scrolls in (opacity tied to scrollY); on the rejected view
// scroll is locked, so scrollY stays 0 and it remains fully visible.
// `onDismiss` (onboarding only) renders the inline cross next to the text.
export function CbccTopBar({
	tone,
	onDismiss,
}: {
	tone: CbccBannerTone;
	onDismiss?: () => void;
}) {
	// Fade with scroll. SSR/first paint = fully visible (scrollY 0). Rejected
	// never scrolls (locked), so it stays at opacity 1.
	const [scrollY, setScrollY] = useState(0);
	useEffect(() => {
		const onScroll = () => setScrollY(window.scrollY || 0);
		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);
	const FADE_PX = 120; // fully faded after ~120px of scroll
	const opacity = Math.max(0, 1 - scrollY / FADE_PX);

	// rejected = red; onboarding = ixigo-credit BRAND orange (#F47B20, same as the
	// apply CTA). White text on both for contrast.
	const bg = tone === 'rejected' ? 'bg-critical-solid' : 'bg-brand-solid';
	const text =
		tone === 'rejected' ? CBCC_BANNERS.rejected : CBCC_BANNERS.onboardingResume;
	return (
		<div
			// aria-live (not role="status") so screen readers announce the banner
			// when it appears, without the role/element mismatch lint wants.
			aria-live="polite"
			// Capped at 1920 + mx-auto so the banner matches the page's max width
			// (the hero canvas maxCanvasWidth=1920 / manage page max-w-[1920px]):
			// past 1920 it stays centered with the same black/gradient gutters as the
			// content, instead of stretching edge-to-edge. inset-x-0 + mx-auto centers
			// the capped bar within the fixed viewport.
			className={`fixed inset-x-0 top-0 z-[60] mx-auto flex w-full max-w-[1920px] items-center justify-center gap-2 ${bg} px-10 text-center text-sm font-medium text-white`}
			style={{
				height: CBCC_BANNER_HEIGHT,
				opacity,
				transition: 'opacity 120ms linear',
				// once invisible, let clicks pass through to the page beneath.
				pointerEvents: opacity <= 0.02 ? 'none' : undefined,
			}}
		>
			<span>{text}</span>
			{onDismiss ? (
				// inline (not absolute) so the cross sits right next to the text with a
				// small gap, and the text+cross group stays centered together.
				<button
					type="button"
					aria-label="Dismiss"
					onClick={onDismiss}
					className="shrink-0 text-white transition-colors hover:text-white"
				>
					✕
				</button>
			) : null}
		</div>
	);
}
